import {
  createSlice,
  createAsyncThunk,
  createListenerMiddleware,
  isAnyOf,
} from '@reduxjs/toolkit';
import { instance } from '@app/api/axios';
import { defaultUsersParams } from '@utils/tables/params';
import { showToaster } from '@shared/ui/messages/useToaster';
import i18n from '@app/i18n';

export const fetchGroupById = createAsyncThunk(
  'group_id/fetchGroupById',
  async (_, { rejectWithValue, getState }) => {
    const { params, search } = getState().group_id;

    const merged = { ...defaultUsersParams, ...params };
    const query = {
      page: merged.page,
      per_page: merged.per_page,
      sort_by: merged.sort_by,
      sort_dir: merged.sort_dir,
      slug: merged.slug,
      ...(search && { search }),
    };

    try {
      const res = await instance.get('/fetch_group_by_slug', { params: query });
      return res.data;
    } catch (err) {
      showToaster(
        'error',
        err?.response?.data?.message || i18n.t('request_error'),
      );
      return rejectWithValue(err.response?.status);
    }
  },
);

const groupIdSlice = createSlice({
  name: 'group_id',
  initialState: {
    users: [],
    count_total: 0,
    count_filtered: 0,
    params: defaultUsersParams,
    search: '',
    group: null,
    isLoading: true,
    isInitialized: false,
    isNotFound: false,
  },
  reducers: {
    setGroupIdParams: (state, action) => {
      state.params = { ...state.params, ...action.payload };
    },
    setGroupIdSearch: (state, action) => {
      state.search = action.payload;
      state.params = { ...state.params, page: 1 };
      if (state.users?.length === 0) {
        state.isInitialized = false;
      }
    },
    resetGroupIdState: (state) => {
      state.users = [];
      state.count_total = 0;
      state.count_filtered = 0;
      state.group = null;
      state.search = '';
      state.isNotFound = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGroupById.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchGroupById.fulfilled, (state, action) => {
      if (state.group?.slug !== action.payload?.group?.slug) {
        state.search = '';
      }
      state.isLoading = false;
      state.isInitialized = true;
      state.group = action.payload?.group;
      state.users = action.payload?.group?.users;
      state.count_total = action.payload?.count_total;
      state.count_filtered = action.payload?.count_filtered;
    });
    builder.addCase(fetchGroupById.rejected, (state, action) => {
      state.isLoading = false;
      state.isNotFound = action.payload === 404;
    });
  },
});

export const { setGroupIdParams, setGroupIdSearch, resetGroupIdState } =
  groupIdSlice.actions;

export default groupIdSlice.reducer;

export const groupIdListener = createListenerMiddleware();

groupIdListener.startListening({
  matcher: isAnyOf(setGroupIdParams, setGroupIdSearch),
  effect: async (_, listenerApi) => {
    await listenerApi.dispatch(fetchGroupById());
  },
});
